import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../../translations/LanguageContext";

// Sample texts live as plain .txt files in public/sample-texts. Each file
// starts with a small metadata header, then a "---" line, then the text
// itself:
//
//   title: Bore da
//   level: A1
//   language: cy
//   description: A short, simple greeting and introduction — ideal for beginners.
//   ---
const SAMPLE_TEXTS_DIR = "/sample-texts";
const INDEX_URL = `${SAMPLE_TEXTS_DIR}/index.json`;
const HEADER_DELIMITER = /\r?\n-{3,}\r?\n/;
const EXCERPT_LENGTH = 140;

const LANGUAGES = [
    { code: "cy", labelKey: "analysePage.sampleTextsWelsh" },
    { code: "en", labelKey: "analysePage.sampleTextsEnglish" },
];

function parseSampleFile(filename, raw) {
    const [header, ...bodyParts] = raw.split(HEADER_DELIMITER);
    const body = bodyParts.join("\n").trim();

    const meta = {};
    header.split(/\r?\n/).forEach(line => {
        const separatorIndex = line.indexOf(":");
        if (separatorIndex === -1) return;
        const key = line.slice(0, separatorIndex).trim().toLowerCase();
        const value = line.slice(separatorIndex + 1).trim();
        if (key) meta[key] = value;
    });

    return {
        file: filename,
        title: meta.title || filename,
        level: meta.level || "",
        language: meta.language || "cy",
        description: meta.description || "",
        text: body,
    };
}

// mode: "samples" — shown in place of the InputBox/TextAnnotator.
export function SampleTexts({ onAnalyse, onBack }) {
    const { t } = useTranslation();
    const [samples, setSamples] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [language, setLanguage] = useState("cy");

    useEffect(() => {
        let cancelled = false;

        async function loadSamples() {
            setLoading(true);
            setError(null);
            try {
                const indexRes = await fetch(INDEX_URL);
                if (!indexRes.ok) throw new Error("Failed to load sample text index");
                const filenames = await indexRes.json();

                const parsed = await Promise.all(
                    filenames.map(async filename => {
                        const res = await fetch(`${SAMPLE_TEXTS_DIR}/${filename}`);
                        if (!res.ok) throw new Error(`Failed to load ${filename}`);
                        const raw = await res.text();
                        return parseSampleFile(filename, raw);
                    })
                );

                if (!cancelled) {
                    setSamples(parsed.sort((a, b) => a.title.localeCompare(b.title)));
                }
            } catch (err) {
                console.error(err);
                if (!cancelled) setError(t("analysePage.sampleTextsError"));
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        loadSamples();
        return () => {
            cancelled = true;
        };
    }, [t]);

    // How many samples exist per language, so the toggle can show counts
    // (and so we know if a language tab would be empty).
    const counts = useMemo(() => {
        return samples.reduce((acc, sample) => {
            acc[sample.language] = (acc[sample.language] || 0) + 1;
            return acc;
        }, {});
    }, [samples]);

    const visibleSamples = useMemo(
        () => samples.filter(sample => sample.language === language),
        [samples, language]
    );

    return (
        <div className="text-input sample-texts">
            <div className="input-section input-top">
                <span className="input-txt">{t("analysePage.sampleText")}</span>
                <button className="secondary-btn" onClick={onBack}>
                    {t("common.back")}
                </button>
            </div>

            {!loading && !error && samples.length > 0 && (
                <div className="button-wrap sample-lang-toggle" role="tablist">
                    {LANGUAGES.map(lang => (
                        <button
                            key={lang.code}
                            role="tab"
                            aria-selected={language === lang.code}
                            className={`toggle-btn ${language === lang.code ? "toggle-btn--active" : ""}`}
                            onClick={() => setLanguage(lang.code)}
                        >
                            {t(lang.labelKey)}
                            <span className="sample-lang-count">{counts[lang.code] || 0}</span>
                        </button>
                    ))}
                </div>
            )}

            {loading && (
                <div className="sample-list-status">{t("loading.sub")}</div>
            )}

            {!loading && error && (
                <div className="error-banner" role="alert">{error}</div>
            )}

            {!loading && !error && samples.length === 0 && (
                <div className="sample-list-status">{t("analysePage.sampleTextsEmpty")}</div>
            )}

            {!loading && !error && samples.length > 0 && visibleSamples.length === 0 && (
                <div className="sample-list-status">
                    {t("analysePage.sampleTextsEmptyForLanguage")}
                </div>
            )}

            {!loading && !error && visibleSamples.length > 0 && (
                <div className="sample-list">
                    {visibleSamples.map(sample => (
                        <div className="sample-card" key={sample.file}>
                            <div className="sample-card-header">
                                <span className="sample-card-title">{sample.title}</span>
                                {sample.level && (
                                    <span className="sample-card-level">{sample.level}</span>
                                )}
                            </div>
                            {sample.description && (
                                <p className="sample-card-desc">{sample.description}</p>
                            )}
                            <p className="sample-card-excerpt">
                                {sample.text.length > EXCERPT_LENGTH
                                    ? `${sample.text.slice(0, EXCERPT_LENGTH)}…`
                                    : sample.text}
                            </p>
                            <div className="sample-card-actions">
                                <button
                                    className="action-btn"
                                    onClick={() => onAnalyse(sample.text, sample.language)}
                                >
                                    {sample.language === "en"
                                        ? t("analysePage.analyseEn")
                                        : t("analysePage.analyseCy")}
                                    <span className="action-arrow">→</span>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}